import { Order, OrderReturnStatus, OrderStatus, OrderType } from '../../../clients/quipli-service/order/order';
import { useState, useEffect } from 'react';
import { ApproveOrder } from './approve-order';
import { Button } from '../../../components/form/button';
import { Location } from '../../../clients/quipli-service/location/location';
import { MissedOrder, RejectOrder } from './reject-order';
import { toast } from '../../../util/toasts';
import { ApproveQuote } from '../approve-quote/approve-quote-v2';
import { OnRentOrder } from './on-rent-order';
import { OffRentOrder } from './off-rent-v2/off-rent-order';
import { InventoryReturn } from './inventory-return';
import { ReopenOrder } from './reopen-order';
import Icon from '../../../components/icons/icon';
import { Dropdown } from '../../../components/dropdown/dropdown';
import { Typography } from '../../../components/typography/typography';
import { DispatchStatusTransitionButton } from '../../dispatch-v2/dispatch-status-transition-button';
import { CollectPaymentModal } from './shared/components/collect-payment-modal/collect-payment-modal';
import { useStatusTransitionConfig } from './hooks/use-status-transition-config';
import { shouldPaymentModalShow, StatusTransition } from './utils/status-transition-utils';

/*
  This is a bit of an uncommon pattern, but it has some benefits and for this particular use case, it's better than using context
  - Essentially this is to handle the payment modal coming after the inventory return,
    but some components using this will re-render after the inventory return call, so we just need to
    persist this at a module level (it'll clear if you navigate or refresh, which is what we want)

  - This will be revised with a future ticket to change how the payment modal is handled, it may need an additional order status/state
*/
const pendingPaymentModals = new Map<number, { transition: StatusTransition; anyProductsInventoryReturned: boolean }>();

// Test utility to clear module-level state between tests, otherwise very difficult to test
export const _testClearPendingPaymentModals = () => pendingPaymentModals.clear();

export const StatusTransitionButtonsV2 = ({
  order,
  location,
  isDisabled,
  reload,
  hideTransitions = [],
}: {
  order: Order;
  location: Location;
  isDisabled: boolean;
  reload: () => void;
  hideTransitions?: StatusTransition[];
}) => {
  const [activeStatusModal, setActiveStatusModal] = useState<StatusTransition | undefined>();
  const { allowCombinedOffRentReturn } = useStatusTransitionConfig();

  useEffect(() => {
    const pending = pendingPaymentModals.get(order.id);
    if (pending) {
      const { transition } = pending;
      const shouldShow = shouldPaymentModalShow(allowCombinedOffRentReturn, transition);
      if (shouldShow) {
        setActiveStatusModal('collect-payment');
        pendingPaymentModals.delete(order.id);
      }
    }
  }, [order.products.map((p) => p.status).join(',')]);

  const conditionallyShowPaymentModal = (
    transition: StatusTransition,
    anyProductsInventoryReturned: boolean = true,
  ) => {
    const shouldShow = shouldPaymentModalShow(allowCombinedOffRentReturn, transition);
    if (shouldShow) {
      pendingPaymentModals.set(order.id, { transition, anyProductsInventoryReturned });
      reload();
    } else {
      setActiveStatusModal(undefined);
      reload();
    }
  };

  const showCancel =
    !hideTransitions.includes('reject') &&
    ![OrderStatus.Cancelled, OrderStatus.Missed].includes(order.status) &&
    !order.products.every((p) => p.status === OrderReturnStatus.Complete);
  const showReopen =
    !hideTransitions.includes('reopen') &&
    ((order.type === OrderType.Rental && [OrderStatus.Cancelled, OrderStatus.Missed].includes(order.status)) ||
      order.products.some(
        (op) =>
          op.status &&
          [OrderReturnStatus.OnRent, OrderReturnStatus.OffRent, OrderReturnStatus.Complete].includes(op.status),
      ));
  const showMissed = !hideTransitions.includes('missed') && order.status === OrderStatus.Requested;

  return order ? (
    <div className="order-status-transitions w-full">
      <div className="flex justify-between gap-x-[12px] px-[0.6rem] sm:px-0 [&>*]:grow md:[&>*]:grow-0">
        <div className="flex flex-row gap-4">
          {order.dispatches?.length && order.documentType == 'Order' ? (
            <DispatchStatusTransitionButton orderDispatch={order.dispatches.find((_) => _)!} reload={reload} />
          ) : (
            <></>
          )}
          {!hideTransitions.includes('quote') && order.status === OrderStatus.Quote && (
            <Button
              className="button button-yellow"
              onClick={() => setActiveStatusModal('quote')}
              disabled={isDisabled}
              dataCy="approve-quote"
            >
              Approve Quote
            </Button>
          )}

          {!hideTransitions.includes('approve') && order.status === OrderStatus.Requested && (
            <Button
              className="button button-yellow"
              onClick={() => setActiveStatusModal('approve')}
              disabled={isDisabled}
              dataCy="approve-order"
            >
              Approve Order
            </Button>
          )}

          {!hideTransitions.includes('on-rent') &&
            order.status === OrderStatus.Approved &&
            order.products.some(
              (p) => p.type === OrderType.Rental && (!p.status || p.status === OrderReturnStatus.NotStarted),
            ) && (
              <Button
                className="button button-primary"
                onClick={() => setActiveStatusModal('on-rent')}
                disabled={isDisabled}
              >
                On Rent
              </Button>
            )}

          {!hideTransitions.includes('off-rent') &&
            order.type === OrderType.Rental &&
            order.status === OrderStatus.Approved &&
            order.products.some((op) => op.status === OrderReturnStatus.OnRent) && (
              <Button className="button-yellow" onClick={() => setActiveStatusModal('off-rent')} disabled={isDisabled}>
                Off Rent
              </Button>
            )}

          {!hideTransitions.includes('inventory-return') &&
            order.type === OrderType.Rental &&
            order.status === OrderStatus.Approved &&
            order.products.some((op) => op.status === OrderReturnStatus.OffRent) && (
              <Button
                className="button-yellow"
                onClick={() => setActiveStatusModal('inventory-return')}
                disabled={isDisabled}
              >
                Return
              </Button>
            )}
        </div>

        {(showCancel || showReopen || showMissed) && (
          <div className="!grow-0">
            <Dropdown
              trigger={() => (
                <Button className="icon-button">
                  <Icon icon="DotsVertical" size="medium" />
                </Button>
              )}
            >
              {showReopen && (
                <div onClick={() => !isDisabled && setActiveStatusModal('reopen')} data-cy="revert-to-previous-status">
                  <Typography type="text-sm" className="text-quipli-black-800">
                    Revert To Previous Status
                  </Typography>
                </div>
              )}
              {showCancel && (
                <div onClick={() => !isDisabled && setActiveStatusModal('reject')} data-cy="reject-order">
                  <Typography type="text-sm" className="text-quipli-black-800">
                    {order.status === OrderStatus.Quote ? 'Reject Quote' : 'Cancel Order'}
                  </Typography>
                </div>
              )}
              {showMissed && (
                <div onClick={() => !isDisabled && setActiveStatusModal('missed')} data-cy="mark-as-missed-order">
                  <Typography type="text-sm" className="text-quipli-black-800">
                    Mark As Missed Order
                  </Typography>
                </div>
              )}
            </Dropdown>
          </div>
        )}
      </div>

      {activeStatusModal === 'approve' && (
        <ApproveOrder
          orderId={order.id}
          onCancel={() => setActiveStatusModal(undefined)}
          onComplete={() => {
            reload();
            toast('Order approved');
            setActiveStatusModal(undefined);
          }}
        />
      )}

      {activeStatusModal === 'on-rent' && (
        <OnRentOrder
          orderId={order.id}
          onCancel={() => setActiveStatusModal(undefined)}
          onComplete={() => {
            reload();
            toast('Order on rent');
            setActiveStatusModal(undefined);
          }}
        />
      )}

      {activeStatusModal === 'off-rent' && (
        <OffRentOrder
          orderId={order.id}
          onCancel={() => {
            reload();
            setActiveStatusModal(undefined);
          }}
          onComplete={async (anyProductsInventoryReturned: boolean) => {
            toast('Order off rent');
            conditionallyShowPaymentModal('off-rent', anyProductsInventoryReturned);
          }}
        />
      )}

      {activeStatusModal === 'inventory-return' && (
        <InventoryReturn
          orderId={order.id}
          onCancel={() => setActiveStatusModal(undefined)}
          onComplete={async () => {
            toast('Inventory Returned');
            conditionallyShowPaymentModal('inventory-return', true);
          }}
        />
      )}

      {activeStatusModal === 'collect-payment' && (
        <CollectPaymentModal
          orderId={order.id}
          onCancel={() => {
            reload();
            setActiveStatusModal(undefined);
          }}
          onComplete={() => {
            reload();
            setActiveStatusModal(undefined);
          }}
        />
      )}

      {activeStatusModal === 'quote' && (
        <ApproveQuote
          order={order}
          location={location}
          onCancel={() => setActiveStatusModal(undefined)}
          onComplete={() => {
            reload();
            setActiveStatusModal(undefined);
          }}
        />
      )}

      {activeStatusModal === 'reject' && (
        <RejectOrder
          order={order}
          location={location}
          onConfirm={() => reload()}
          onClose={() => setActiveStatusModal(undefined)}
        />
      )}

      {activeStatusModal === 'missed' && (
        <MissedOrder
          order={order}
          location={location}
          onConfirm={() => reload()}
          onClose={() => setActiveStatusModal(undefined)}
        />
      )}

      {activeStatusModal === 'reopen' && (
        <ReopenOrder
          order={order}
          onCancel={() => setActiveStatusModal(undefined)}
          onComplete={() => {
            reload();
            setActiveStatusModal(undefined);
          }}
        />
      )}
    </div>
  ) : (
    <></>
  );
};
